import React, { useState, useEffect, useContext } from 'react';
import { string, bool, shape } from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Carousel } from '@thd-olt-component-react/carousel';
import { useHelmet, LinkTag } from '@thd-nucleus/thd-helmet';

import classNames from 'classnames/bind';
import { makeApiRequest, shouldMakeApiRequest } from '../../utils/requestHelpers';
import { initImpressionWatchers } from '../../utils/initImpressionWatchers';
import {
  getSchema,
  getSkus,
  getDefaultBanner,
  triggerNewRelic,
  parseIfJson,
  isBrandFpn
} from '../../utils/helpers';

import styles from './SponsoredBanner.module.scss';
import { newRelicConstants } from '../../constants';

let cx = classNames.bind(styles);

const hoistedImgFunction = ({ data }) => {
  const { isBrowse, avoidPreload, defaultBannerInfo } = data;
  if (isBrowse && !avoidPreload) {
    return [
      new LinkTag({
        rel: 'preload',
        as: 'image',
        href: defaultBannerInfo.imgSrc,
        id: 'spBannerPreloadLink',
      }),
    ];
  }
  return null;
};

const SponsoredBanner = (props) => {

  let {
    avoidPreload, categoryId, pageContext, schema, slugId, utilizeRvData, showSponsoredBanner,
  } = props;
  const [lastRequestScenario, setLastRequestScenario] = useState({});
  const [hasData, setHasData] = useState(false);
  const experienceContext = useContext(ExperienceContext);
  const defaultBannerInfo = getDefaultBanner(experienceContext);
  const [isBranded, setIsBranded] = useState(isBrandFpn({ experienceContext, pageContext }));
  schema = getSchema({ schema, pageContext });
  const schemaId = schema + '__banner';

  const data = {
    avoidPreload,
    channel: experienceContext.channel,
    isBrowse: pageContext?.isBrowse,
    defaultBannerInfo,
  };

  const onRevjetMessage = (evt) => {
    try {
      const messageData = parseIfJson(evt.data);
      if (
        messageData.module === 'REVJET_Creative'
        && messageData.method === 'dispatchEvent'
        && messageData.properties?.eventObject?.type === 'click'
      ) {
        triggerNewRelic(newRelicConstants.STANDARD_BANNER, newRelicConstants.CLICK);
      }
    } catch (event) {
      throw new Error('Failed to send click to New Relic');
    }
  };

  useHelmet('preloadBanner', { data }, hoistedImgFunction);

  useEffect(() => {

    const startTime = new Date().getTime();
    // For browse, search, category and event page schemas
    if (pageContext?.label === 'browse-search') {
      const isValidScenario = shouldMakeApiRequest({
        lastRequestScenario,
        experienceContext,
        pageContext,
      });

      if (isValidScenario) {
        setLastRequestScenario({
          url: window.location.pathname + window.location.search,
          skus: getSkus(pageContext),
        });
        makeApiRequest({
          schema,
          schemaId,
          experienceContext,
          pageContext,
          setHasData,
          utilizeRvData,
          startTime,
          categoryId,
          slugId,
        });
      }
    } else if (showSponsoredBanner) {
      //  All other schemas
      makeApiRequest({
        schema,
        schemaId,
        experienceContext,
        pageContext,
        setHasData,
        utilizeRvData,
        startTime,
        categoryId,
        slugId,
      });
    }

  }, [pageContext?.data, categoryId, slugId, schema]);

  useEffect(() => {
    window.addEventListener('message', onRevjetMessage, false);

    return () => {
      window.removeEventListener('message', onRevjetMessage);
    };
  }, []);

  useEffect(() => {
    initImpressionWatchers(['NUCLEUS_BANNER']);
  }, [hasData]);

  if (isBranded) {
    return null;
  }

  return (
    <div
      className={cx('sponsored_banner_container')}
      style={defaultBannerInfo.bgCss}
      id="sponsored-standard-banner-nucleus"
    >
      {!hasData && (
        <div
          id="default_banner_carousel"
          style={defaultBannerInfo.spacingCss}
          data-testid="default-standard-banner"
        >
          <a href={defaultBannerInfo.href}>
            {/* eslint-disable-next-line */}
            <img
              width="100%"
              src={defaultBannerInfo.imgSrc}
              style={{ position: 'absolute', top: 0, left: 0 }}
            />
          </a>
        </div>
      )}

      {hasData && (
        <Carousel multiItem={false} totalItems={2}>
          <div
            id="default_banner_carousel"
            style={defaultBannerInfo.spacingCss}
            data-testid="default-standard-banner"
          >
            <a href={defaultBannerInfo.href}>
              {/* eslint-disable-next-line */}
              <img
                width="100%"
                src={defaultBannerInfo.imgSrc}
                style={{ position: 'absolute', top: 0, left: 0 }}
              />
            </a>
          </div>
          <div
            id={schemaId}
            className="nucleus-banner"
            data-usenewimpressionlogic="true"
            data-testid="sponsored-standard-banner"
          />
        </Carousel>
      )}
    </div>
  );
};

SponsoredBanner.propTypes = {
  categoryId: string,
  schema: string,
  slugId: string,
  pageContext: shape({
    label: string,
    data: shape(),
    keyword: string,
    isSearch: bool,
    isCategory: bool,
    isBrowse: bool,
  }),
  avoidPreload: bool,
  utilizeRvData: bool,
  showSponsoredBanner: bool

};

SponsoredBanner.defaultProps = {
  avoidPreload: true,
  categoryId: '',
  pageContext: {},
  schema: '',
  slugId: '',
  utilizeRvData: false,
  showSponsoredBanner: true
};

export { SponsoredBanner };
